import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  GlobalStyle,
  Page,
  Container,
  Hero,
  Eyebrow,
  HeroTitle,
  HeroMeta,
  HeroLede,
  AcceptBanner,
  Layout,
  TocWrap,
  TocTitle,
  TocList,
  TocLink,
  Content,
  Section,
  SectionTitle,
  SubTitle,
  P,
  List,
  ListItem,
  Callout,
  CriticalCallout,
  CriticalLabel,
  TableWrap,
  Table,
  ContactList,
  ContactRow,
  BackHome,
} from "./infoPageStyles";

const TOC = [
  { id: "overview", label: "1. Overview" },
  { id: "report", label: "2. Reporting a User" },
  { id: "report-message", label: "3. Reporting a Message" },
  { id: "block", label: "4. Blocking & Unmatching" },
  { id: "non-users", label: "5. Reporting Without an Account" },
  { id: "what-happens", label: "6. What Happens Next" },
  { id: "outcomes", label: "7. Enforcement Outcomes" },
  { id: "red-flag", label: "8. The Red Flag System" },
  { id: "appeals", label: "9. Appeals" },
  { id: "emergency", label: "10. Emergencies" },
  { id: "contact", label: "11. Contact Us" },
];

export default function SafetyToolsPage() {
  const [active, setActive] = useState(TOC[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Page>
      <GlobalStyle />

      <Hero>
        <Container>
          <Eyebrow>Safety</Eyebrow>
          <HeroTitle>Reporting &amp; Blocking</HeroTitle>
          <HeroMeta>
            <span>Effective Date: August 11, 2026</span>
          </HeroMeta>
          <HeroLede>
            Every NudR member can report a profile, report a specific message,
            or block another member — at any time, for any reason. This page
            explains where those tools live in the app, what happens after you
            use them, and how enforcement decisions are made.
          </HeroLede>
          <AcceptBanner>
            You never need a reason to block someone, and you never need to
            interact with someone who makes you uncomfortable. Blocking is
            immediate and the other person is not told you blocked them.
          </AcceptBanner>
        </Container>
      </Hero>

      <Container>
        <Layout>
          <TocWrap>
            <TocTitle>On this page</TocTitle>
            <TocList>
              {TOC.map((item) => (
                <li key={item.id}>
                  <TocLink href={`#${item.id}`} $active={active === item.id}>
                    {item.label}
                  </TocLink>
                </li>
              ))}
            </TocList>
          </TocWrap>

          <Content>
            <Section id="overview">
              <SectionTitle>
                <span>01</span>Overview
              </SectionTitle>
              <P>
                NudR gives you three separate tools, and you can use any
                combination of them:
              </P>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Tool</th>
                      <th>What it does</th>
                      <th>Is the other person told?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Report</td>
                      <td>
                        Sends the profile, message, or media to our safety team
                        for review.
                      </td>
                      <td>No — reports are confidential.</td>
                    </tr>
                    <tr>
                      <td>Block</td>
                      <td>
                        Stops all contact immediately and removes them from your
                        feed.
                      </td>
                      <td>No.</td>
                    </tr>
                    <tr>
                      <td>Unmatch</td>
                      <td>
                        Ends the match and the conversation, without a full
                        block.
                      </td>
                      <td>
                        They'll see the conversation has ended, but get no
                        notification.
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </TableWrap>
              <P>
                Reporting and blocking are independent. Blocking someone doesn't
                report them, and reporting someone doesn't automatically block
                them — so if you want both, use both.
              </P>
            </Section>

            <Section id="report">
              <SectionTitle>
                <span>02</span>Reporting a User
              </SectionTitle>
              <P>
                Open the profile you want to report, tap the menu icon in the
                top corner, and choose <strong>Report</strong>. You'll be asked
                to pick a reason and can add details.
              </P>
              <SubTitle>Reasons you can report</SubTitle>
              <List>
                <ListItem>Harassment, threats, or abusive behaviour</ListItem>
                <ListItem>Sexually explicit content</ListItem>
                <ListItem>Someone who appears to be under 18</ListItem>
                <ListItem>Fake profile, impersonation, or catfishing</ListItem>
                <ListItem>
                  Scam, fraud, or requests for money or financial information
                </ListItem>
                <ListItem>Hate speech or discrimination</ListItem>
                <ListItem>
                  Sharing someone's private information without consent
                </ListItem>
                <ListItem>Commercial solicitation or spam</ListItem>
                <ListItem>Off-platform behaviour, including after meeting</ListItem>
                <ListItem>Something else</ListItem>
              </List>
              <P>
                The more detail you give, the faster we can act. If you're
                reporting something that happened in conversation, report the
                specific message rather than just the profile — see the next
                section.
              </P>
            </Section>

            <Section id="report-message">
              <SectionTitle>
                <span>03</span>Reporting a Message
              </SectionTitle>
              <P>
                Press and hold any message in a conversation, then choose{" "}
                <strong>Report</strong>. This works for text, images, voice
                notes, and video.
              </P>
              <Callout>
                <P>
                  <strong>Why this matters.</strong> Messages on NudR are
                  end-to-end encrypted, which means we can't read them in the
                  normal course of running the app. Reporting a specific message
                  is what allows our safety team to see that content and act on
                  it. Reporting only the profile gives us far less to work with.
                </P>
              </Callout>
              <P>
                When you report a message, that message and reasonable
                surrounding context are decrypted and made available to our
                safety team solely to investigate your report, as described in
                our <Link to="/terms">Terms of Service</Link>.
              </P>
              <P>
                Messages and shared media are otherwise deleted automatically —
                within 24 hours of being read, or 30 days if never read. If
                something concerning happens,{" "}
                <strong>report it promptly</strong>; once content is deleted we
                cannot recover it.
              </P>
            </Section>

            <Section id="block">
              <SectionTitle>
                <span>04</span>Blocking &amp; Unmatching
              </SectionTitle>
              <SubTitle>Blocking</SubTitle>
              <P>
                Open their profile or your conversation, tap the menu icon, and
                choose <strong>Block</strong>. Blocking takes effect
                immediately:
              </P>
              <List>
                <ListItem>They can't message you.</ListItem>
                <ListItem>You won't appear in each other's feeds.</ListItem>
                <ListItem>Any existing match between you is ended.</ListItem>
                <ListItem>They are not notified that you blocked them.</ListItem>
              </List>
              <SubTitle>Unmatching</SubTitle>
              <P>
                If you'd rather just end a conversation without a full block,
                choose <strong>Unmatch</strong>. This ends the match and the
                chat, but doesn't prevent them appearing in discovery again
                later.
              </P>
              <SubTitle>If someone won't leave you alone</SubTitle>
              <P>
                Creating new accounts to contact someone who blocked them is a
                serious violation of our{" "}
                <Link to="/guidelines">Community Guidelines</Link> and may
                constitute stalking or harassment under applicable law. If this
                happens to you, report it and contact{" "}
                <a href="mailto:security@nudr.app">security@nudr.app</a>{" "}
                directly.
              </P>
            </Section>

            <Section id="non-users">
              <SectionTitle>
                <span>05</span>Reporting Without an Account
              </SectionTitle>
              <P>
                <strong>
                  You don't need a NudR account to report something to us.
                </strong>{" "}
                If you're not a member — or can't sign in — email{" "}
                <a href="mailto:security@nudr.app">security@nudr.app</a> and our
                safety team will handle it the same way as an in-app report.
              </P>
              <SubTitle>Common reasons people contact us from outside</SubTitle>
              <List>
                <ListItem>
                  <strong>Someone is using your photos.</strong> If your
                  pictures appear on a NudR profile that isn't yours, tell us —
                  impersonation is a violation of our{" "}
                  <Link to="/guidelines">Community Guidelines</Link> and we act
                  on it whether or not you're a member.
                </ListItem>
                <ListItem>
                  <strong>Someone is impersonating you</strong> under your name
                  or identity.
                </ListItem>
                <ListItem>
                  <strong>Intimate images of you were shared without your
                  consent.</strong> Mark your email urgent — this is treated as
                  a priority.
                </ListItem>
                <ListItem>
                  <strong>You're concerned about someone on the platform</strong>{" "}
                  — including suspected trafficking, exploitation, or a person
                  who may be under 18.
                </ListItem>
                <ListItem>
                  <strong>You're a parent, guardian, or advocate</strong>{" "}
                  reporting on behalf of someone else.
                </ListItem>
              </List>
              <P>
                Include whatever you can: the profile name or link, a
                description of the issue, and — if you're reporting misuse of
                your own photos or identity — a way for us to verify that the
                images are of you. We'll never publish or share what you send
                with the person you're reporting.
              </P>
              <Callout>
                <P>
                  For <strong>child safety</strong> concerns, email{" "}
                  <a href="mailto:childsafety@nudr.app">childsafety@nudr.app</a>{" "}
                  — see our{" "}
                  <Link to="/child-safety">Child Safety Standards</Link>. For{" "}
                  <strong>privacy or data</strong> requests, use{" "}
                  <a href="mailto:privacy@nudr.app">privacy@nudr.app</a>.
                </P>
              </Callout>
            </Section>

            <Section id="what-happens">
              <SectionTitle>
                <span>06</span>What Happens After You Report
              </SectionTitle>
              <List>
                <ListItem>
                  <strong>Your report is queued for review.</strong> Reports are
                  prioritised by severity — anything involving a suspected
                  minor, credible threats, or non-consensual intimate content
                  goes to the front.
                </ListItem>
                <ListItem>
                  <strong>Our safety team reviews the content</strong> against
                  our Community Guidelines and Terms of Service.
                </ListItem>
                <ListItem>
                  <strong>We take action</strong> where the review finds a
                  violation. See the next section for the range of outcomes.
                </ListItem>
                <ListItem>
                  <strong>Your report stays confidential.</strong> We don't tell
                  the person who reported them.
                </ListItem>
              </List>
              <SubTitle>Our response times</SubTitle>
              <P>
                Our safety team is staffed{" "}
                <strong>24 hours a day, 7 days a week</strong>. Reports are
                triaged on arrival and prioritised by severity, and we work to
                the following targets:
              </P>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Report type</th>
                      <th>We aim to act within</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        Child safety concerns, and any threat of immediate harm
                      </td>
                      <td>1 hour</td>
                    </tr>
                    <tr>
                      <td>
                        Trafficking, threats of violence, and intimate images
                        shared without consent
                      </td>
                      <td>24 hours</td>
                    </tr>
                    <tr>
                      <td>
                        All other reports — harassment, hate speech, explicit
                        content, fake profiles, spam
                      </td>
                      <td>72 hours</td>
                    </tr>
                  </tbody>
                </Table>
              </TableWrap>
              <P>
                Because reports are confidential, we may not be able to tell you
                the outcome of a report you made about someone else.
              </P>
              <P>
                Knowingly false, malicious, or retaliatory reporting is itself a
                violation of our Community Guidelines.
              </P>
            </Section>

            <Section id="outcomes">
              <SectionTitle>
                <span>07</span>Enforcement Outcomes
              </SectionTitle>
              <P>
                Depending on severity, context, and history, a confirmed
                violation can result in:
              </P>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Outcome</th>
                      <th>When it applies</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Warning</td>
                      <td>Minor, first-time violations.</td>
                    </tr>
                    <tr>
                      <td>Content removal</td>
                      <td>
                        Violating photographs, bio text, or media are removed or
                        hidden.
                      </td>
                    </tr>
                    <tr>
                      <td>Feature restriction</td>
                      <td>
                        Temporary loss of media sharing, messaging, or discovery
                        visibility.
                      </td>
                    </tr>
                    <tr>
                      <td>Temporary suspension</td>
                      <td>Account access paused for a set period.</td>
                    </tr>
                    <tr>
                      <td>Permanent termination</td>
                      <td>
                        Serious or repeated violations. No refund of prepaid
                        amounts.
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </TableWrap>
              <P>
                Some categories skip this ladder entirely and result in
                immediate permanent termination — content involving minors,
                non-consensual intimate content, romance scams and financial
                fraud, credible threats of violence, and human trafficking. In
                those cases we also preserve evidence and refer to law
                enforcement where required.
              </P>
            </Section>

            <Section id="red-flag">
              <SectionTitle>
                <span>08</span>The Red Flag System
              </SectionTitle>
              <P>
                Red Flags are NudR's community accountability signal. When
                members report behaviour that our safety team confirms, that
                account accrues a flag.
              </P>
              <List>
                <ListItem>
                  Flags accumulate against an account and inform enforcement
                  decisions.
                </ListItem>
                <ListItem>
                  Accumulated flags escalate the response, up to permanent
                  termination.
                </ListItem>
                <ListItem>
                  Flags follow the account, not the device — creating a new
                  account to escape them is ban evasion and is prohibited.
                </ListItem>
              </List>
              <P>
                Flags are applied after safety-team review, not automatically on
                report. This protects members from coordinated or retaliatory
                reporting.
              </P>
            </Section>

            <Section id="appeals">
              <SectionTitle>
                <span>09</span>Appeals
              </SectionTitle>
              <P>
                If enforcement action is taken against your account and you
                believe it was a mistake, you can submit{" "}
                <strong>one appeal per action</strong> by emailing{" "}
                <a href="mailto:security@nudr.app">security@nudr.app</a> from
                the address on your account, within 30 days. Include what
                happened and why you think the decision was wrong.
              </P>
              <P>
                We acknowledge appeals within{" "}
                <strong>2 business days</strong> and aim to reach a decision
                within <strong>10 business days</strong>. We review appeals in
                good faith. Appeal decisions are final.{" "}
                <strong>
                  No appeal is available for enforcement relating to child
                  safety
                </strong>{" "}
                — see our <Link to="/child-safety">Child Safety Standards</Link>.
              </P>
            </Section>

            <Section id="emergency">
              <SectionTitle>
                <span>10</span>Emergencies
              </SectionTitle>
              <CriticalCallout>
                <CriticalLabel>If you're in immediate danger</CriticalLabel>
                <P>
                  <strong>Contact your local emergency services first.</strong>{" "}
                  In the United States, call <strong>911</strong>. Report to us
                  afterwards so we can preserve evidence and act on the account.
                  We are not an emergency service and cannot dispatch help.
                </P>
              </CriticalCallout>
              <P>
                For urgent safety matters that aren't an immediate emergency —
                threats, stalking, or non-consensual intimate content — email{" "}
                <a href="mailto:security@nudr.app">security@nudr.app</a> as well
                as reporting in the app.
              </P>
              <P>
                US support lines, available 24/7 and independent of NudR:
              </P>
              <List>
                <ListItem>
                  <strong>988</strong> — Suicide &amp; Crisis Lifeline (call or
                  text)
                </ListItem>
                <ListItem>
                  <strong>Text HOME to 741741</strong> — Crisis Text Line
                </ListItem>
                <ListItem>
                  <strong>1-800-656-4673</strong> — RAINN, for survivors of
                  sexual assault
                </ListItem>
                <ListItem>
                  <strong>1-800-799-7233</strong> — National Domestic Violence
                  Hotline
                </ListItem>
                <ListItem>
                  <strong>1-888-373-7888</strong> — National Human Trafficking
                  Hotline (or text 233733)
                </ListItem>
              </List>
            </Section>

            <Section id="contact">
              <SectionTitle>
                <span>11</span>Contact Us
              </SectionTitle>
              <ContactList>
                <ContactRow>
                  <b>Trust &amp; Safety:</b>{" "}
                  <a href="mailto:security@nudr.app">security@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>Child Safety:</b>{" "}
                  <a href="mailto:childsafety@nudr.app">childsafety@nudr.app</a>
                </ContactRow>
                <ContactRow>
                  <b>General Support:</b>{" "}
                  <a href="mailto:support@nudr.app">support@nudr.app</a>
                </ContactRow>
              </ContactList>

              <P style={{ marginTop: "1.4rem", fontSize: "0.85rem" }}>
                © {new Date().getFullYear()} NudR. All rights reserved.
              </P>

              <BackHome to="/">← Back to home</BackHome>
            </Section>
          </Content>
        </Layout>
      </Container>
    </Page>
  );
}
